import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthProvider.tsx";
import { Bootstrap, SignIn } from "./auth/AuthScreens.tsx";
import { AppShell } from "./components/AppShell.tsx";
import { SendingIdentityProvider } from "./components/SendingIdentity.tsx";
import { AccountPage } from "./pages/Account.tsx";
import { BrandsPage } from "./pages/Brands.tsx";
import { CampaignReportPage } from "./pages/CampaignReport.tsx";
import { CampaignsPage } from "./pages/Campaigns.tsx";
import { ComposerPage } from "./pages/Composer.tsx";
import { ContactsPage } from "./pages/Contacts.tsx";
import { DashboardPage } from "./pages/Dashboard.tsx";
import { DomainsPage } from "./pages/Domains.tsx";
import { ImportsPage } from "./pages/Imports.tsx";
import { MediaPage } from "./pages/Media.tsx";
import { PostalPage } from "./pages/Postal.tsx";
import { PulseSettingsPage } from "./pages/PulseSettings.tsx";
import { RepliesPage } from "./pages/Replies.tsx";
import { SequencesPage } from "./pages/Sequences.tsx";
import { TeamPage } from "./pages/Team.tsx";
import { TemplatesPage } from "./pages/Templates.tsx";
import { TestSendPage } from "./pages/TestSend.tsx";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Config changes rarely and is read on most screens; refetching it on
      // every window focus is noise rather than freshness.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: false,
    },
  },
});

function Routed() {
  const { user, loading, needsBootstrap } = useAuth();

  if (loading) return <div className="auth-screen" />;
  if (needsBootstrap) return <Bootstrap />;
  if (!user) return <SignIn />;

  return (
    <SendingIdentityProvider>
      <AppShell>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/campaigns/:id" element={<CampaignReportPage />} />
          <Route path="/sequences" element={<SequencesPage />} />
          <Route path="/replies" element={<RepliesPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/templates/:id" element={<ComposerPage />} />
          <Route path="/media" element={<MediaPage />} />
          <Route path="/test-send" element={<TestSendPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/imports" element={<ImportsPage />} />
          <Route path="/settings/brands" element={<BrandsPage />} />
          <Route path="/settings/postal" element={<PostalPage />} />
          <Route path="/settings/pulse" element={<PulseSettingsPage />} />
          <Route path="/settings/domains" element={<DomainsPage />} />
          <Route path="/settings/team" element={<TeamPage />} />
          <Route path="/settings/account" element={<AccountPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </SendingIdentityProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routed />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}
